import { useEffect, useState } from "react";
import {
  checkPastDueSubscription,
  type PastDueSubscriptionResponse,
} from "../services/BillingService";
import { useAuthContext } from "../context/AuthContext";
import { getTokensFromStorage } from "../services/AuthService";

export default function PastDueSubscriptionModal() {
  const { logout } = useAuthContext();
  const [pastDueData, setPastDueData] =
    useState<PastDueSubscriptionResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    // Only check if user is logged in
    const tokens = getTokensFromStorage();
    if (tokens) {
      loadPastDueStatus();
    }
  }, []);

  const loadPastDueStatus = async () => {
    setIsLoading(true);
    try {
      const data = await checkPastDueSubscription();
      setPastDueData(data);

      // If user needs reinstatement, don't show modal (SetUpPage handles this)
      if (data.needsReinstatement) {
        setShouldShow(false);
        return;
      }

      // Show modal only if past due (not for reinstatement)
      setShouldShow(data.hasPastDueSubscription);
    } catch (error) {
      console.error("Failed to check past due subscription:", error);
      setShouldShow(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePayment = () => {
    if (pastDueData?.billingPortalLink) {
      window.open(pastDueData.billingPortalLink, "_blank");
    }
  };

  const handleLogout = () => {
    logout();
    setShouldShow(false);
  };

  // Don't show modal if not needed or if loading
  if (!shouldShow || isLoading) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="text-center">
          {/* Warning Icon for past due */}
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4 bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
          </div>

          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Payment Required
          </h3>

          <p className="text-sm text-gray-500 mb-6">
            Your subscription payment is past due. Please update your payment
            method to continue using DealHawk Pro.
          </p>

          <div className="space-y-3">
            <button
              onClick={handleUpdatePayment}
              disabled={!pastDueData?.billingPortalLink}
              className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-md transition-colors"
            >
              Update Payment Method
            </button>

            <button
              onClick={handleLogout}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-md transition-colors"
            >
              Log Out
            </button>
          </div>

          {pastDueData?.subscriptionId && (
            <p className="text-xs text-gray-400 mt-4">
              Subscription ID: {pastDueData.subscriptionId}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
